// Hook para usar Plausible Analytics
import React, { useCallback, useRef } from 'react';
import { shouldSendEvent, incrementSessionCounter, ANALYTICS_CONFIG } from '../utils/analyticsConfig';

// Rate limiting configuration - usar do config
const RATE_LIMIT = ANALYTICS_CONFIG.RATE_LIMIT;

// Cache para controle de rate limiting
const eventCache = new Map();
let eventQueue = [];
let lastFlush = Date.now();

export const usePlausible = () => {
  const rateLimitRef = useRef(new Map());
  
  // Função para verificar rate limit
  const checkRateLimit = useCallback((eventName) => {
    const now = Date.now();
    const key = eventName;
    const lastEvent = rateLimitRef.current.get(key);
    
    // Verificar cooldown específico do evento
    if (lastEvent && (now - lastEvent) < RATE_LIMIT.cooldownMs) {
      console.warn(`⚡ Rate limit: ${eventName} em cooldown`);
      return false;
    }
    
    // Verificar limite geral de eventos
    const recentEvents = Array.from(eventCache.values())
      .filter(timestamp => (now - timestamp) < RATE_LIMIT.windowMs);
    
    if (recentEvents.length >= RATE_LIMIT.maxEvents) {
      console.warn(`🚦 Rate limit: Muitos eventos (${recentEvents.length}/${RATE_LIMIT.maxEvents})`);
      return false;
    }
    
    return true;
  }, []);

  // Função principal de tracking com proteções - TEMPORARIAMENTE DESABILITADA
  const trackEvent = useCallback((eventName, props = {}) => {
    // 🚫 TODOS OS EVENTS/GOALS TEMPORARIAMENTE DESABILITADOS
    if (process.env.NODE_ENV === 'development') {
      console.log(`🚫 Event DISABLED: ${eventName}`, props);
    }
    return;
    
    /* CÓDIGO COMENTADO - EVENTS/GOALS
    try {
      // Verificar se deve enviar o evento (nova validação)
      if (!shouldSendEvent(eventName, 'event')) {
        console.warn(`🚫 Evento bloqueado: ${eventName}`);
        return;
      }
      
      // Verificar disponibilidade do Plausible
      if (typeof window === 'undefined' || !window.plausible) {
        console.warn('🔇 Plausible não disponível');
        return;
      }
      
      // Verificar rate limiting
      if (!checkRateLimit(eventName)) {
        return;
      }
      
      const now = Date.now();
      
      // Atualizar cache de rate limiting
      rateLimitRef.current.set(eventName, now);
      eventCache.set(`${eventName}_${now}`, now);
      
      // Incrementar contador de sessão
      incrementSessionCounter('event');
      
      // Enviar evento com fallback
      const eventData = { 
        props: {
          ...props,
          timestamp: new Date().toISOString(),
          domain: window.location.hostname
        }
      };
      
      window.plausible(eventName, eventData);
      
      // Log de sucesso (apenas em desenvolvimento)
      if (ANALYTICS_CONFIG.ENVIRONMENT.debugMode) {
        console.log(`📊 Plausible: ${eventName}`, props);
      }
      
    } catch (error) {
      console.error(`❌ Erro Plausible: ${eventName}`, error);
      
      // Em caso de erro, adicionar à queue para retry
      if (eventQueue.length < 50) { // Limite da queue
        eventQueue.push({ eventName, props, timestamp: Date.now() });
      }
    }
    */
  }, []);

  // Eventos específicos para o Dash Simples
  const trackFileUpload = useCallback((chartType, fileType) => {
    trackEvent('File Upload', {
      chart_type: chartType,
      file_type: fileType
    });
  }, [trackEvent]);

  // GOALS TEMPORARIAMENTE DESABILITADOS
  const trackChartGeneration = useCallback((chartType, itemCount) => {
    // 🚫 GOALS TEMPORARIAMENTE DESABILITADOS
    if (process.env.NODE_ENV === 'development') {
      console.log(`🚫 Goal DISABLED: Chart Generated - ${chartType} (${itemCount} items)`);
    }
    return;
    
    /* CÓDIGO COMENTADO - GOALS
    trackEvent('Chart Generated', {
      chart_type: chartType,
      item_count: itemCount
    });
    */
  }, []);

  const trackRoadmapGeneration = useCallback((category, stepCount) => {
    // 🚫 GOALS TEMPORARIAMENTE DESABILITADOS
    if (process.env.NODE_ENV === 'development') {
      console.log(`🚫 Goal DISABLED: Roadmap Generated - ${category} (${stepCount} steps)`);
    }
    return;
    
    /* CÓDIGO COMENTADO - GOALS
    trackEvent('Roadmap Generated', {
      category: category,
      step_count: stepCount
    });
    */
  }, []);

  const trackExampleFileUsage = useCallback((chartType) => {
    trackEvent('Example File Used', {
      chart_type: chartType
    });
  }, [trackEvent]);

  const trackExtractorInterest = useCallback((tool) => {
    trackEvent('Extractor Interest', {
      tool: tool
    });
  }, [trackEvent]);

  const trackFeedbackSent = useCallback((type) => {
    trackEvent('Feedback Sent', {
      type: type
    });
  }, [trackEvent]);

  const trackButtonClick = useCallback((buttonName, context) => {
    trackEvent('Button Click', {
      button: buttonName,
      context: context
    });
  }, [trackEvent]);

  // Export tracking - PNG/XLSX exports
  const trackExport = useCallback((exportType, chartType, context = '') => {
    trackEvent('Chart Export', {
      export_type: exportType, // 'png' ou 'xlsx'
      chart_type: chartType,   // 'leadtime', 'throughput', 'roadmap'
      context: context
    });
  }, [trackEvent]);

  // File type tracking - CSV vs XLSX usage
  const trackFileType = useCallback((fileType, chartType, source = 'upload') => {
    trackEvent('File Type Used', {
      file_type: fileType,     // 'csv' ou 'xlsx'
      chart_type: chartType,
      source: source           // 'upload' ou 'example'
    });
  }, [trackEvent]);

  // Download tracking - Example files vs own data usage
  const trackDownload = useCallback((downloadType, chartType, leadTimeType = null) => {
    trackEvent('File Download', {
      download_type: downloadType,  // 'example' ou 'export'
      chart_type: chartType,
      leadtime_type: leadTimeType
    });
  }, [trackEvent]);

  // Session and engagement tracking
  const trackSectionTime = useCallback((sectionName, timeSpent) => {
    trackEvent('Section Time', {
      section: sectionName,
      time_spent: Math.round(timeSpent), // em segundos
      engagement_level: timeSpent > 60 ? 'high' : timeSpent > 30 ? 'medium' : 'low'
    });
  }, [trackEvent]);

  const trackAbandon = useCallback((sectionName, reason = '', progressPercentage = 0) => {
    trackEvent('Section Abandon', {
      section: sectionName,
      reason: reason,           // 'error', 'confusion', 'slow_load', 'manual'
      progress: Math.round(progressPercentage),
      session_duration: Date.now() - (window.sessionStartTime || Date.now())
    });
  }, [trackEvent]);

  // Error tracking
  const trackUploadError = useCallback((errorType, chartType, errorDetails = {}) => {
    trackEvent('Upload Error', {
      error_type: errorType,    // 'format', 'size', 'columns', 'encoding', 'corrupted'
      chart_type: chartType,
      file_size: errorDetails.fileSize || 0,
      file_type: errorDetails.fileType || '',
      error_message: errorDetails.message || '',
      retry_count: errorDetails.retryCount || 0
    });
  }, [trackEvent]);

  const trackValidationError = useCallback((chartType, validationResults = {}) => {
    trackEvent('Validation Error', {
      chart_type: chartType,
      error_count: validationResults.errorCount || 0,
      missing_columns: validationResults.missingColumns || [],
      invalid_dates: validationResults.invalidDates || 0,
      invalid_values: validationResults.invalidValues || 0,
      empty_rows: validationResults.emptyRows || 0,
      total_rows: validationResults.totalRows || 0,
      success_rate: validationResults.successRate || 0
    });
  }, [trackEvent]);

  const trackPerformance = useCallback((action, duration, details = {}) => {
    trackEvent('Performance', {
      action: action,           // 'file_parse', 'chart_render', 'export_generate'
      duration: Math.round(duration),
      file_size: details.fileSize || 0,
      row_count: details.rowCount || 0,
      chart_complexity: details.complexity || 'simple'
    });
  }, [trackEvent]);

  // Virtual Page Tracking para SPA
    const trackVirtualPage = useCallback((virtualPath) => {
    if (window.plausible) {
      const fullPath = `/virtual${virtualPath}`;
      window.plausible('pageview', { u: `${window.location.origin}${fullPath}` });
    }
  }, []);

  // Virtual page tracking específico para seções do Dash Simples - TEMPORARIAMENTE DESABILITADO
  const trackSection = useCallback((sectionName, customProps = {}) => {
    // 🚫 VIRTUAL PAGES TEMPORARIAMENTE DESABILITADAS
    if (process.env.NODE_ENV === 'development') {
      console.log(`🚫 Virtual Page DISABLED: ${sectionName}`, customProps);
    }
    return;
    
    /* CÓDIGO COMENTADO - VIRTUAL PAGES
    const sectionMap = {
      'home': '/home',
      'chart-selector': '/chart-selector',
      'leadtime-selection': '/leadtime-selection',
      'leadtime-analysis': '/leadtime/analysis',
      'leadtime-breakdown': '/leadtime/breakdown',
      'leadtime-analysis-generated': '/leadtime/analysis/generated',
      'leadtime-breakdown-generated': '/leadtime/breakdown/generated',
      'throughput-selection': '/throughput-selection',
      'throughput-generated': '/throughput/generated',
      'roadmap-selection': '/roadmap-selection',
      'roadmap-view': '/roadmap/view',
      'roadmap-create': '/roadmap/create',
      'roadmap-analysis': '/roadmap/analysis',
      'how-it-works': '/how-it-works',
      // Upload tracking
      'upload-csv': '/upload/csv',
      'upload-xlsx': '/upload/xlsx',
      'upload-example': '/upload/example',
      // Export actions
      'export-png': '/export/png',
      'export-xlsx': '/export/xlsx',
      // Download actions  
      'download-example': '/download/example',
      'download-export': '/download/export',
      // Error tracking
      'error-upload': '/error/upload',
      'error-validation': '/error/validation',
      'error-processing': '/error/processing',
      // Engagement tracking  
      'section-abandon': '/engagement/abandon',
      'section-complete': '/engagement/complete'
    };
    
    const virtualPath = sectionMap[sectionName];
    if (!virtualPath) {
      console.warn(`🚫 Seção não mapeada: ${sectionName}`);
      return;
    }
    
    // Verificar se Plausible está disponível
    if (!window.plausible) {
      console.warn('🔇 Plausible não disponível para virtual page');
      return;
    }
    
    // NOVA ABORDAGEM: history.pushState + pageview normal
    try {
      // Evitar duplicatas apenas dentro de um período curto (30s)
      const recentPageKey = `recent_page_${sectionName}`;
      const lastSent = sessionStorage.getItem(recentPageKey);
      const now = Date.now();
      
      if (lastSent && (now - parseInt(lastSent)) < 30000) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`🚫 Virtual page muito recente: ${sectionName} (${Math.round((now - parseInt(lastSent))/1000)}s)`);
        }
        return;
      }
      
      // Marcar como enviado recentemente
      sessionStorage.setItem(recentPageKey, now.toString());
      
      // MÉTODO QUE FUNCIONA: history.pushState + pageview normal
      const originalUrl = window.location.href;
      const originalPath = window.location.pathname;
      
      // Temporariamente alterar URL
      history.pushState({}, '', virtualPath);
      
      // Enviar pageview normal (sem parâmetros!)
      window.plausible('pageview');
      
      // Log sempre para debug das virtual pages
      if (process.env.NODE_ENV === 'development') {
        console.log(`📍 Virtual Page Sent: ${virtualPath}`);
      }
      
      // Restaurar URL original após delay mínimo (quase imperceptível)
      setTimeout(() => {
        history.pushState({}, '', originalPath);
      }, 200);
      
      // Incrementar contador apenas para estatísticas
      incrementSessionCounter('pageview');
      
      // Enviar evento adicional apenas se houver props importantes
      if (Object.keys(customProps).length > 0) {
        setTimeout(() => {
          trackEvent('Section View', {
            section: sectionName,
            virtual_path: virtualPath,
            ...customProps
          });
        }, 1500); // Delay maior para evitar conflitos
      }
      
    } catch (error) {
      console.error(`❌ Erro ao enviar virtual page ${sectionName}:`, error);
    }
    */
  }, []);

  // Sistema de limpeza automática de cache
  const cleanupCache = useCallback(() => {
    const now = Date.now();
    
    // Limpar eventos antigos do cache
    for (const [key, timestamp] of eventCache.entries()) {
      if ((now - timestamp) > RATE_LIMIT.windowMs) {
        eventCache.delete(key);
      }
    }
    
    // Limpar rate limit refs antigos
    for (const [key, timestamp] of rateLimitRef.current.entries()) {
      if ((now - timestamp) > RATE_LIMIT.cooldownMs) {
        rateLimitRef.current.delete(key);
      }
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`🧹 Cache limpo: ${eventCache.size} eventos ativos`);
    }
  }, []);

  // Sistema de retry para eventos falhados
  const processRetryQueue = useCallback(() => {
    if (eventQueue.length === 0) return;
    
    const now = Date.now();
    const retryEvents = eventQueue.filter(item => 
      (now - item.timestamp) > RATE_LIMIT.cooldownMs
    );
    
    retryEvents.slice(0, 3).forEach(item => { // Máximo 3 retries por vez
      if (checkRateLimit(item.eventName)) {
        try {
          window.plausible?.(item.eventName, { props: item.props });
          if (process.env.NODE_ENV === 'development') {
            console.log(`♻️ Retry sucesso: ${item.eventName}`);
          }
          
          // Remover da queue
          const index = eventQueue.indexOf(item);
          if (index > -1) eventQueue.splice(index, 1);
          
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.warn(`♻️ Retry falhou: ${item.eventName}`, error);
          }
          // Manter na queue se ainda não expirou
          if ((now - item.timestamp) > 300000) { // 5 minutos
            const index = eventQueue.indexOf(item);
            if (index > -1) eventQueue.splice(index, 1);
          }
        }
      }
    });
  }, [checkRateLimit]);

  // Executar limpeza periodicamente
  React.useEffect(() => {
    const interval = setInterval(() => {
      cleanupCache();
      processRetryQueue();
    }, 30000); // A cada 30 segundos
    
    return () => clearInterval(interval);
  }, [cleanupCache, processRetryQueue]);

  return {
    trackEvent,
    trackFileUpload,
    trackChartGeneration,
    trackRoadmapGeneration,
    trackExampleFileUsage,
    trackExtractorInterest,
    trackFeedbackSent,
    trackButtonClick,
    trackVirtualPage,
    trackSection,
    // New tracking functions
    trackExport,
    trackFileType,
    trackDownload,
    // Advanced tracking functions
    trackSectionTime,
    trackAbandon,
    trackUploadError,
    trackValidationError,
    trackPerformance
  };
};
